import {
  type FC,
  type FormEvent,
  useEffect,
  useState,
} from 'react';

import {
  useAuth,
} from '@/contexts/AuthContext';

type ChangePasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ChangePasswordModal: FC<
  ChangePasswordModalProps
> = ({
  isOpen,
  onClose,
}) => {
  const {
    changePassword,
  } = useAuth();

  const [
    currentPassword,
    setCurrentPassword,
  ] = useState('');

  const [
    newPassword,
    setNewPassword,
  ] = useState('');

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState('');

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    success,
    setSuccess,
  ] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setSaving(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit =
    async (
      event: FormEvent
    ) => {
      event.preventDefault();

      setError('');
      setSuccess('');

      if (
        newPassword.length < 10
      ) {
        setError(
          'A nova senha deve possuir pelo menos 10 caracteres.'
        );
        return;
      }

      if (
        newPassword !==
        confirmPassword
      ) {
        setError(
          'A confirmação da nova senha não confere.'
        );
        return;
      }

      setSaving(true);

      try {
        await changePassword(
          currentPassword,
          newPassword
        );

        setSuccess(
          'Senha alterada com sucesso. As demais sessões foram revogadas.'
        );

        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } catch (
        changeError
      ) {
        setError(
          changeError instanceof
            Error
            ? changeError.message
            : 'Não foi possível alterar a senha.'
        );
      } finally {
        setSaving(false);
      }
    };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 cursor-default"
        onClick={() => {
          if (!saving) {
            onClose();
          }
        }}
      />

      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-md overflow-hidden rounded-xl border border-background-200 bg-background-50 shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground-900">
              Alterar senha
            </h2>

            <p className="mt-1 text-xs text-foreground-500">
              A alteração revoga as outras sessões abertas.
            </p>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 disabled:opacity-50"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {success ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {success}
            </div>
          ) : null}

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-foreground-600">
              Senha atual
            </span>

            <input
              type="password"
              value={currentPassword}
              onChange={(event) =>
                setCurrentPassword(
                  event.target.value
                )
              }
              autoComplete="current-password"
              required
              className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-foreground-600">
              Nova senha
            </span>

            <input
              type="password"
              value={newPassword}
              onChange={(event) =>
                setNewPassword(
                  event.target.value
                )
              }
              autoComplete="new-password"
              required
              minLength={10}
              className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-foreground-600">
              Confirmar nova senha
            </span>

            <input
              type="password"
              value={confirmPassword}
              onChange={(event) =>
                setConfirmPassword(
                  event.target.value
                )
              }
              autoComplete="new-password"
              required
              minLength={10}
              className="w-full rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none"
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-background-200 px-5 py-4">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-lg border border-background-200 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 disabled:opacity-50"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:opacity-50"
          >
            {saving
              ? 'Alterando...'
              : 'Alterar senha'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordModal;
